/**
 * Lookup tables shared by the two directions of the LaTeX ↔ Unicode converter:
 * superscript and subscript characters, the mathematical alphabets (`\mathbb`,
 * `\mathcal`, …), accent marks, and the symbol tables built from the Symbol Finder's
 * data (one source of truth for `\alpha` ↔ α).
 */

import {
  LATEX_SYMBOLS,
  type LatexSymbol,
  type SymbolPackage,
} from "@/lib/tool-kit/developer/latex-symbols";

/** Characters that have a Unicode superscript form. */
export const SUPERSCRIPTS: Readonly<Record<string, string>> = {
  "0": "⁰",
  "1": "¹",
  "2": "²",
  "3": "³",
  "4": "⁴",
  "5": "⁵",
  "6": "⁶",
  "7": "⁷",
  "8": "⁸",
  "9": "⁹",
  "+": "⁺",
  "-": "⁻",
  "−": "⁻",
  "=": "⁼",
  "(": "⁽",
  ")": "⁾",
  a: "ᵃ",
  b: "ᵇ",
  c: "ᶜ",
  d: "ᵈ",
  e: "ᵉ",
  f: "ᶠ",
  g: "ᵍ",
  h: "ʰ",
  i: "ⁱ",
  j: "ʲ",
  k: "ᵏ",
  l: "ˡ",
  m: "ᵐ",
  n: "ⁿ",
  o: "ᵒ",
  p: "ᵖ",
  r: "ʳ",
  s: "ˢ",
  t: "ᵗ",
  u: "ᵘ",
  v: "ᵛ",
  w: "ʷ",
  x: "ˣ",
  y: "ʸ",
  z: "ᶻ",
  A: "ᴬ",
  B: "ᴮ",
  D: "ᴰ",
  E: "ᴱ",
  G: "ᴳ",
  H: "ᴴ",
  I: "ᴵ",
  J: "ᴶ",
  K: "ᴷ",
  L: "ᴸ",
  M: "ᴹ",
  N: "ᴺ",
  O: "ᴼ",
  P: "ᴾ",
  R: "ᴿ",
  T: "ᵀ",
  U: "ᵁ",
  V: "ⱽ",
  W: "ᵂ",
  α: "ᵅ",
  β: "ᵝ",
  γ: "ᵞ",
  δ: "ᵟ",
  θ: "ᶿ",
  φ: "ᵠ",
  χ: "ᵡ",
};

/** Characters already raised by their own design, which stay as they are after `^`. */
export const SUPERSCRIPT_PASSTHROUGH: Readonly<Record<string, string>> = {
  "∘": "°",
  "°": "°",
  "′": "′",
  "″": "″",
  "‴": "‴",
};

/** Characters that have a Unicode subscript form. */
export const SUBSCRIPTS: Readonly<Record<string, string>> = {
  "0": "₀",
  "1": "₁",
  "2": "₂",
  "3": "₃",
  "4": "₄",
  "5": "₅",
  "6": "₆",
  "7": "₇",
  "8": "₈",
  "9": "₉",
  "+": "₊",
  "-": "₋",
  "−": "₋",
  "=": "₌",
  "(": "₍",
  ")": "₎",
  a: "ₐ",
  e: "ₑ",
  h: "ₕ",
  i: "ᵢ",
  j: "ⱼ",
  k: "ₖ",
  l: "ₗ",
  m: "ₘ",
  n: "ₙ",
  o: "ₒ",
  p: "ₚ",
  r: "ᵣ",
  s: "ₛ",
  t: "ₜ",
  u: "ᵤ",
  v: "ᵥ",
  x: "ₓ",
  β: "ᵦ",
  γ: "ᵧ",
  ρ: "ᵨ",
  φ: "ᵩ",
  χ: "ᵪ",
};

/**
 * Map every character of `text` through a script table; `null` when any character has
 * no form, so the caller can leave the whole group alone instead of half-converting it.
 */
export function mapScript(
  text: string,
  table: Readonly<Record<string, string>>,
  passthrough: Readonly<Record<string, string>> = {}
): string | null {
  let out = "";
  for (const ch of text) {
    const mapped = table[ch] ?? passthrough[ch];
    if (mapped === undefined) {
      return null;
    }
    out += mapped;
  }
  return out;
}

function invert(
  table: Readonly<Record<string, string>>
): ReadonlyMap<string, string> {
  const out = new Map<string, string>();
  for (const [key, value] of Object.entries(table)) {
    if (key !== "−" && !out.has(value)) {
      out.set(value, key);
    }
  }
  return out;
}

/** Unicode superscript character → the plain character it stands for. */
export const SUPERSCRIPT_REVERSE = invert(SUPERSCRIPTS);
/** Unicode subscript character → the plain character it stands for. */
export const SUBSCRIPT_REVERSE = invert(SUBSCRIPTS);

/** Vulgar fractions, keyed "numerator/denominator". */
export const VULGAR_FRACTIONS: Readonly<Record<string, string>> = {
  "1/2": "½",
  "1/3": "⅓",
  "2/3": "⅔",
  "1/4": "¼",
  "3/4": "¾",
  "1/5": "⅕",
  "2/5": "⅖",
  "3/5": "⅗",
  "4/5": "⅘",
  "1/6": "⅙",
  "5/6": "⅚",
  "1/7": "⅐",
  "1/8": "⅛",
  "3/8": "⅜",
  "5/8": "⅝",
  "7/8": "⅞",
  "1/9": "⅑",
  "1/10": "⅒",
};

export const VULGAR_REVERSE: ReadonlyMap<string, readonly [string, string]> =
  new Map(
    Object.entries(VULGAR_FRACTIONS).map(([ratio, ch]) => {
      const [top, bottom] = ratio.split("/");
      return [ch, [top, bottom] as const];
    })
  );

// ---------------------------------------------------------------------------
// Mathematical alphabets
// ---------------------------------------------------------------------------

export type AlphabetStyle =
  | "bold"
  | "italic"
  | "script"
  | "fraktur"
  | "double"
  | "sans"
  | "mono";

interface AlphabetSpec {
  upper: number;
  lower: number;
  digits?: number;
  /** Letters Unicode placed in the Letterlike Symbols block instead. */
  exceptions: Readonly<Record<string, string>>;
}

const ALPHABET_SPECS: Readonly<Record<AlphabetStyle, AlphabetSpec>> = {
  bold: {
    upper: 0x1_d4_00,
    lower: 0x1_d4_1a,
    digits: 0x1_d7_ce,
    exceptions: {},
  },
  italic: { upper: 0x1_d4_34, lower: 0x1_d4_4e, exceptions: { h: "ℎ" } },
  script: {
    upper: 0x1_d4_9c,
    lower: 0x1_d4_b6,
    exceptions: {
      B: "ℬ",
      E: "ℰ",
      F: "ℱ",
      H: "ℋ",
      I: "ℐ",
      L: "ℒ",
      M: "ℳ",
      R: "ℛ",
      e: "ℯ",
      g: "ℊ",
      o: "ℴ",
    },
  },
  fraktur: {
    upper: 0x1_d5_04,
    lower: 0x1_d5_1e,
    exceptions: { C: "ℭ", H: "ℌ", I: "ℑ", R: "ℜ", Z: "ℨ" },
  },
  double: {
    upper: 0x1_d5_38,
    lower: 0x1_d5_52,
    digits: 0x1_d7_d8,
    exceptions: { C: "ℂ", H: "ℍ", N: "ℕ", P: "ℙ", Q: "ℚ", R: "ℝ", Z: "ℤ" },
  },
  sans: {
    upper: 0x1_d5_a0,
    lower: 0x1_d5_ba,
    digits: 0x1_d7_e2,
    exceptions: {},
  },
  mono: {
    upper: 0x1_d6_70,
    lower: 0x1_d6_8a,
    digits: 0x1_d7_f6,
    exceptions: {},
  },
};

function buildAlphabet(spec: AlphabetSpec): Map<string, string> {
  const out = new Map<string, string>();
  for (let i = 0; i < 26; i++) {
    const upper = String.fromCharCode(65 + i);
    const lower = String.fromCharCode(97 + i);
    out.set(
      upper,
      spec.exceptions[upper] ?? String.fromCodePoint(spec.upper + i)
    );
    out.set(
      lower,
      spec.exceptions[lower] ?? String.fromCodePoint(spec.lower + i)
    );
  }
  if (spec.digits !== undefined) {
    for (let d = 0; d < 10; d++) {
      out.set(String(d), String.fromCodePoint(spec.digits + d));
    }
  }
  return out;
}

export const ALPHABETS: Readonly<
  Record<AlphabetStyle, ReadonlyMap<string, string>>
> = {
  bold: buildAlphabet(ALPHABET_SPECS.bold),
  italic: buildAlphabet(ALPHABET_SPECS.italic),
  script: buildAlphabet(ALPHABET_SPECS.script),
  fraktur: buildAlphabet(ALPHABET_SPECS.fraktur),
  double: buildAlphabet(ALPHABET_SPECS.double),
  sans: buildAlphabet(ALPHABET_SPECS.sans),
  mono: buildAlphabet(ALPHABET_SPECS.mono),
};

/** LaTeX font commands that select a mathematical alphabet. */
export const STYLE_COMMANDS: Readonly<Record<string, AlphabetStyle>> = {
  mathbb: "double",
  mathbf: "bold",
  mathcal: "script",
  mathscr: "script",
  mathfrak: "fraktur",
  mathit: "italic",
  mathsf: "sans",
  mathtt: "mono",
};

/** The command written when going back, and the package it needs ("" for none). */
export const STYLE_REVERSE_COMMAND: Readonly<
  Record<AlphabetStyle, { command: string; pkg: string }>
> = {
  bold: { command: "mathbf", pkg: "" },
  italic: { command: "", pkg: "" },
  script: { command: "mathcal", pkg: "" },
  fraktur: { command: "mathfrak", pkg: "amssymb" },
  double: { command: "mathbb", pkg: "amssymb" },
  sans: { command: "mathsf", pkg: "" },
  mono: { command: "mathtt", pkg: "" },
};

/** Mathematical-alphabet character → its style and the plain letter or digit. */
export const ALPHABET_REVERSE: ReadonlyMap<
  string,
  { style: AlphabetStyle; ch: string }
> = (() => {
  const out = new Map<string, { style: AlphabetStyle; ch: string }>();
  for (const style of Object.keys(ALPHABETS) as AlphabetStyle[]) {
    for (const [plain, styled] of ALPHABETS[style]) {
      out.set(styled, { style, ch: plain });
    }
  }
  return out;
})();

// ---------------------------------------------------------------------------
// Accents
// ---------------------------------------------------------------------------

/** Text-mode accent commands → combining marks: `\'`, `\"`, `\c`, `\v` … */
export const TEXT_ACCENT_MARKS: Readonly<Record<string, string>> = {
  "'": "́",
  "`": "̀",
  "^": "̂",
  '"': "̈",
  "~": "̃",
  "=": "̄",
  ".": "̇",
  u: "̆",
  v: "̌",
  H: "̋",
  c: "̧",
  k: "̨",
  r: "̊",
  d: "̣",
  b: "̱",
};

/** Combining mark → text accent command (without its backslash). */
export const TEXT_ACCENT_REVERSE: ReadonlyMap<string, string> = new Map(
  Object.entries(TEXT_ACCENT_MARKS).map(([cmd, mark]) => [mark, cmd])
);

/** Math accent commands → combining marks. */
export const MATH_ACCENT_MARKS: Readonly<Record<string, string>> = {
  hat: "̂",
  widehat: "̂",
  check: "̌",
  tilde: "̃",
  widetilde: "̃",
  acute: "́",
  grave: "̀",
  dot: "̇",
  ddot: "̈",
  dddot: "⃛",
  ddddot: "⃜",
  breve: "̆",
  bar: "̄",
  vec: "⃗",
  overrightarrow: "⃗",
  mathring: "̊",
  overline: "̅",
  underline: "̲",
};

/** Combining mark → math accent command (the first command listed for each mark). */
export const MATH_ACCENT_REVERSE: ReadonlyMap<string, string> = (() => {
  const out = new Map<string, string>();
  for (const [cmd, mark] of Object.entries(MATH_ACCENT_MARKS)) {
    if (!out.has(mark)) {
      out.set(mark, cmd);
    }
  }
  return out;
})();

/** Packages a math accent needs. */
export const MATH_ACCENT_PACKAGE: Readonly<Record<string, string>> = {
  dddot: "amsmath",
  ddddot: "amsmath",
};

/** Text accents that need the T1 font encoding to look right. */
export const T1_ACCENTS: ReadonlySet<string> = new Set(["k"]);

// ---------------------------------------------------------------------------
// Symbols
// ---------------------------------------------------------------------------

const RE_CONTROL_WORD = /^\\[A-Za-z]+$/;
const RE_CONTROL_SYMBOL = /^\\[^A-Za-z]$/;
const RE_LETTER_CHAR = /^\p{L}$/u;

/** Characters the symbol data gives a different, friendlier reading in plain text. */
const FORWARD_OVERRIDES: Readonly<Record<string, string>> = {
  "\\vert": "|",
  "\\lvert": "|",
  "\\rvert": "|",
  "\\Vert": "‖",
  "\\lVert": "‖",
  "\\rVert": "‖",
  "\\|": "‖",
  "\\lbrack": "[",
  "\\rbrack": "]",
  "\\colon": ":",
  "\\ldotp": ".",
  "\\cdotp": "·",
  "\\lq": "‘",
  "\\rq": "’",
  "\\vline": "|",
};

function buildForward(): ReadonlyMap<string, string> {
  const out = new Map<string, string>();
  for (const symbol of LATEX_SYMBOLS) {
    const usable =
      RE_CONTROL_WORD.test(symbol.command) ||
      RE_CONTROL_SYMBOL.test(symbol.command);
    if (usable && !out.has(symbol.command)) {
      out.set(symbol.command, symbol.char);
    }
  }
  for (const [command, char] of Object.entries(FORWARD_OVERRIDES)) {
    out.set(command, char);
  }
  return out;
}

/** `\alpha` → "α", `\&` → "&", … for every command that takes no argument. */
export const FORWARD_SYMBOLS: ReadonlyMap<string, string> = buildForward();

/** Names of text-mode commands that produce a letter (`\ss`, `\o`, `\ae` …). */
export const TEXT_LETTER_COMMANDS: ReadonlySet<string> = new Set(
  LATEX_SYMBOLS.filter(
    (symbol) =>
      symbol.mode === "text" &&
      RE_CONTROL_WORD.test(symbol.command) &&
      RE_LETTER_CHAR.test(symbol.char)
  ).map((symbol) => symbol.command.slice(1))
);

export interface SymbolSide {
  command: string;
  pkg: SymbolPackage;
}

export interface ReverseSymbol {
  math?: SymbolSide;
  text?: SymbolSide;
  /** A binary operator, relation or arrow: it sits between two operands. */
  infix: boolean;
}

const INFIX_CATEGORIES: ReadonlySet<LatexSymbol["category"]> = new Set([
  "binary",
  "relation",
  "negated",
  "arrow",
]);

/** Set and logic symbols that sit between two operands (the rest are prefix or standalone). */
const INFIX_LOGIC: ReadonlySet<string> = new Set([
  "∈",
  "∋",
  "⊂",
  "⊃",
  "⊆",
  "⊇",
  "∧",
  "∨",
  "⟹",
  "⟸",
  "⟺",
]);

/** A package rank for choosing between commands for one character: built-in first. */
function packageRank(pkg: SymbolPackage): number {
  return pkg === "latex" || pkg === "textcomp" ? 0 : 1;
}

function isUsableReverse(symbol: LatexSymbol): boolean {
  if (!RE_CONTROL_WORD.test(symbol.command)) {
    return false;
  }
  const code = symbol.char.codePointAt(0) ?? 0;
  return code > 127 && Array.from(symbol.char).length === 1;
}

function buildReverse(): ReadonlyMap<string, ReverseSymbol> {
  const out = new Map<string, ReverseSymbol>();
  for (const symbol of LATEX_SYMBOLS) {
    if (!isUsableReverse(symbol)) {
      continue;
    }
    const entry = out.get(symbol.char) ?? { infix: false };
    const side = symbol.mode === "math" ? "math" : "text";
    const current = entry[side];
    if (!current || packageRank(symbol.package) < packageRank(current.pkg)) {
      entry[side] = { command: symbol.command, pkg: symbol.package };
    }
    if (
      side === "math" &&
      (INFIX_CATEGORIES.has(symbol.category) || INFIX_LOGIC.has(symbol.char))
    ) {
      entry.infix = true;
    }
    out.set(symbol.char, entry);
  }
  // A norm bar is written \| in LaTeX; the symbol data lists it with the parallel sign.
  out.set("‖", { math: { command: "\\|", pkg: "latex" }, infix: false });
  // "…" is the same character in both modes; \ldots works in each.
  out.set("…", {
    math: { command: "\\ldots", pkg: "latex" },
    text: { command: "\\ldots", pkg: "latex" },
    infix: false,
  });
  return out;
}

/** Character → the LaTeX commands that produce it, by mode. */
export const REVERSE_SYMBOLS: ReadonlyMap<string, ReverseSymbol> =
  buildReverse();

/** Characters that read as maths even though a text command exists for them. */
export const MATH_PREFERRED: ReadonlySet<string> = new Set([
  "×",
  "÷",
  "±",
  "∗",
  "¬",
  "√",
]);

/** `\not` followed by a relation, as one character. */
export const NEGATIONS: Readonly<Record<string, string>> = {
  "=": "≠",
  "<": "≮",
  ">": "≯",
  "\\in": "∉",
  "\\equiv": "≢",
  "\\sim": "≁",
  "\\leq": "≰",
  "\\le": "≰",
  "\\geq": "≱",
  "\\ge": "≱",
  "\\subset": "⊄",
  "\\subseteq": "⊈",
  "\\supset": "⊅",
  "\\supseteq": "⊉",
  "\\approx": "≉",
  "\\cong": "≇",
  "\\ni": "∌",
  "\\mid": "∤",
  "\\parallel": "∦",
  "\\exists": "∄",
};
